/**
 * "Send Document" — the manual send from the patient file (`tasks/practice-documents.md` §7
 * phase 2, §14.3 Phase 2.5c).
 *
 * Reached two ways, both of which end up here: the sidebar action
 * (`patient-file.component.ts` -> `navigate(undefined, 'manageDocument')`) and the panel header's
 * Send button.
 *
 * ── ONE SEND, N DOCUMENTS ────────────────────────────────────────────────────────────────────
 *
 * The single `mat-select` this dialog used to own is gone, replaced by
 * `<app-document-attachment-picker context="manual">` (§14.3). The dialog no longer loads the
 * practice's documents, no longer filters them, and no longer says anything about an individual
 * document — the picker does all three, in the same words the settings tab uses, and it is the
 * thing that says what the WHOLE selection will do (§14.4). What is left here is what this dialog
 * is actually for: the channels, the physician, and the 409.
 *
 * The send posts `practiceDocumentIds` as an ARRAY, always — a send of one is an array of one, and
 * still gets its own `bundleId` (§16).
 *
 * ── WHAT A SEND NOW PRODUCES: AN INVITE, NOT A PER-DOCUMENT LINK (§19, §21.2) ────────────────
 *
 * `POST /patient-document-requests` mints a **document-only `PatientInvite`** carrying everything
 * in the selection that has to come back, and delivers THAT. It answers with `patientInviteId`,
 * and `null` there is not a failure: it means the whole selection was information-only, which goes
 * as email attachments with no link at all (§14) and has no invite to mint.
 *
 * The request body and the refusal codes are unchanged. What changed is the sentence this dialog
 * is allowed to say — "a link was created for this document" is no longer what happens, and the
 * `data-test="send-what-happens"` note in the template says the true thing instead.
 *
 * ── THE 409 IS THE POINT OF THIS COMPONENT ───────────────────────────────────────────────────
 *
 * `POST /patient-document-requests` refuses a duplicate ask with 409 `OPEN_REQUEST`, which comes
 * from the ONE generalised lifecycle (`utils/patientRequestDelivery.js`, §12) rather than a second
 * copy of the rule. It is not an error — it is the correct answer to "send this document":
 *
 *   OPEN_REQUEST  they already have a LIVE link for it; RESEND that one, do not raise a second
 *
 * There were three. `WITHIN_REPEAT_WINDOW` and `COMPLETED_NO_REPEAT` refused because the patient
 * had returned the document before, and §30 deleted both: how often a practice asks a patient for
 * something is the practice's workflow, not ours to refuse.
 *
 * So the dialog STAYS OPEN and shows the sentence, with a button that takes the user to the
 * request that blocked the send. A generic red toast here would send a receptionist to the phone
 * to chase a document the patient already has in their inbox.
 *
 * A refusal NAMES THE DOCUMENT only where one document was chosen. With four in the selection the
 * server's refusal is about one of them and this client does not know which, so it says "this
 * document" rather than naming the wrong one — the same reason `describeDocumentFailure` takes the
 * name as optional.
 *
 * ── `fillMode` IS NEVER PRINTED, HERE EITHER ─────────────────────────────────────────────────
 *
 * §10.2 settled that, and the picker inherits it: what is shown per document is `requiresResponse`,
 * because that is what changes what the patient receives (§14.2).
 */

import { Component, Inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { takeUntil } from 'rxjs';

import { BaseComponent } from '../../../../core/components/base/base.component';
import { FeedbackService } from '../../../../core/services/feedback.service';
import { GroupService } from '../../../../core/services/group.service';
import {
  PatientDocumentRequestService,
  describeDocumentFailure,
} from '../../../../core/services/patient-document-request.service';
import { PracticeDocumentDto } from '../../../../core/services/practice-document.service';
import { UserService } from '../../../../core/services/user.service';

export interface PatientDocumentSendDialogData {
  patient: any;
}

/** What the dialog closes with. `focusRequestId` means "the user asked to see that request". */
export interface PatientDocumentSendDialogResult {
  sent?: boolean;
  focusRequestId?: string;
}

@Component({
  selector: 'app-patient-document-send-dialog',
  templateUrl: './patient-document-send-dialog.component.html',
  styleUrls: ['./patient-document-send-dialog.component.scss'],
})
export class PatientDocumentSendDialogComponent extends BaseComponent implements OnInit {

  form!: FormGroup;
  patient: any;

  /** The picker's `[(selection)]`, and the rows behind it for the sentences below. */
  selectedDocumentIds: string[] = [];
  selectedDocuments: PracticeDocumentDto[] = [];

  groupUsers: any[] = [];

  sending = false;

  /** The refusal currently on screen, if any. Set by a 409/422; cleared on any edit. */
  blockedMessage: string | null = null;
  blockedRequestId: string | null = null;

  constructor(
    private fb: FormBuilder,
    private patientDocumentRequestService: PatientDocumentRequestService,
    private groupService: GroupService,
    private userService: UserService,
    private dialogRef: MatDialogRef<PatientDocumentSendDialogComponent,
      PatientDocumentSendDialogResult>,
    @Inject(MAT_DIALOG_DATA) public data: PatientDocumentSendDialogData,
    fs: FeedbackService,
    md: MatDialog,
  ) {
    super(fs, md);
    this.patient = data ? data.patient : null;
  }

  ngOnInit(): void {
    this.form = this.fb.group({
      physicianId: [''],
      // Email is on by default and SMS is not: email carries the document's name and description
      // and costs the practice nothing, where SMS spends a credit per send.
      email: [this.hasEmail],
      sms: [false],
    });

    // Any edit invalidates the refusal on screen — it was about a different send.
    this.form.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => this.clearRefusal());

    this.loadPhysicians();
  }

  get hasEmail(): boolean {
    return !!(this.patient && this.patient.email);
  }

  get hasMobile(): boolean {
    return !!(this.patient && this.patient.mobileNumber);
  }

  get patientName(): string {
    if (!this.patient) return 'this patient';
    return `${this.patient.name || ''} ${this.patient.surname || ''}`.trim() || 'this patient';
  }

  get patientGroupId(): string | null {
    return this.patient ? this.patient.groupId : null;
  }

  /** At least one document, at least one channel, and a channel the patient can be reached on. */
  get canSend(): boolean {
    if (!this.form || this.form.invalid || this.sending) return false;
    if (!this.selectedDocumentIds.length) return false;
    const value = this.form.value;
    return (value.email && this.hasEmail) || (value.sms && this.hasMobile);
  }

  /* ── The picker ───────────────────────────────────────────────────────────────────────── */

  onSelectionChange(selection: string[]): void {
    this.selectedDocumentIds = selection || [];
    // A refusal was about the documents that were ticked when it was raised.
    this.clearRefusal();
  }

  onSelectedDocumentsChange(documents: PracticeDocumentDto[]): void {
    this.selectedDocuments = documents || [];
  }

  /** Named only where there is ONE — see the component header. */
  private get soleDocumentName(): string | undefined {
    return this.selectedDocuments.length === 1 ? this.selectedDocuments[0].name : undefined;
  }

  /** Same source and same filter the consent dialog uses for its physician picker. */
  private loadPhysicians(): void {
    this.groupService.groupUsers$
      .pipe(takeUntil(this.destroy$))
      .subscribe((users: any[]) => {
        if (!users || !users.length) return;
        this.groupUsers = users.filter((u: any) => u.userType === 'doctor');
        const currentUser = this.userService.user;
        if (currentUser && this.groupUsers.some((gu: any) => gu.userId === currentUser.id)) {
          this.form.patchValue({ physicianId: currentUser.id }, { emitEvent: false });
        }
      });
  }

  send(): void {
    if (!this.canSend || !this.patient) return;

    const value = this.form.value;
    const channels: string[] = [];
    if (value.email && this.hasEmail) channels.push('email');
    if (value.sms && this.hasMobile) channels.push('sms');

    this.sending = true;
    this.clearRefusal();

    const documentName = this.soleDocumentName;

    this.patientDocumentRequestService.createRequest({
      patientId: this.patient.id,
      practiceDocumentIds: this.selectedDocumentIds.slice(),
      physicianId: value.physicianId || null,
      channels,
    })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (result: any) => {
          this.sending = false;
          this.showToastMessage(this.describeDelivery(result, documentName));
          this.dialogRef.close({ sent: true });
        },
        error: (error: any) => {
          this.sending = false;
          const failure = describeDocumentFailure(error, documentName);
          // The dialog stays open. The user has to be able to read what happened and act on it.
          this.blockedMessage = failure.message;
          this.blockedRequestId = failure.existingRequestId || null;
        },
      });
  }

  /**
   * Say what actually went out. The delivery layer never throws and reports per channel, so a
   * document whose email bounced or whose SMS was skipped for want of credits arrives here as a 201
   * with the bad news in `delivery` — reporting a flat "Document sent" would be wrong.
   */
  private describeDelivery(result: any, documentName?: string): string {
    const delivery = result ? result.delivery : null;

    /*
     * THE COUNT COMES FROM THE SERVER, NEVER FROM WHAT WAS TICKED.
     *
     * A 201 does not mean the whole selection went out. Request-time dedupe is applied per
     * document, so a send of three where one is already outstanding creates TWO rows and reports
     * the third in `skipped` — and this method previously counted `selectedDocumentIds`, which
     * would have said "3 documents were sent" about two. That was invisible while the dialog sent
     * one document at a time; the picker is what makes it reachable, and it is the kind of lie
     * that is only discovered when somebody asks the patient why they never got the third one.
     *
     * `requests` is the created set. A response without it is a Phase 2-shaped one, which could
     * only ever have created the single request in `data`.
     */
    const sentCount = Array.isArray(result && result.requests)
      ? result.requests.length
      : (result && result.data ? 1 : this.selectedDocumentIds.length);
    const skipped: any[] = Array.isArray(result && result.skipped) ? result.skipped : [];

    const name = documentName && sentCount === 1
      ? `"${documentName}"`
      : (sentCount > 1 ? `${sentCount} documents` : 'The document');
    const were = sentCount > 1 ? 'were' : 'was';

    /*
     * HOW it went, from the server's own answer rather than from what was ticked.
     *
     * `patientInviteId` present means a document-only invite was minted and delivered (§21.2);
     * absent means the whole selection was information-only and rode the email as attachments
     * (§14). Those are two different things to tell a receptionist — one of them leaves the
     * patient with something to do — so they get two different sentences, and neither of them
     * says "a link was created for this document", which is no longer true of either.
     */
    const how = (result && result.patientInviteId)
      ? ' The patient has been sent an invite containing '
        + (sentCount > 1 ? 'them.' : 'it.')
      : '';

    // Named, not counted. "One was not sent" gives a reader nothing to act on; the name and the
    // server's own sentence are what tell them whether to chase it or leave it.
    const skippedNote = skipped.length
      ? ' ' + skipped
        .map((entry: any) => `"${entry && entry.documentName ? entry.documentName : 'One document'}"`
          + ` was not sent: ${entry && entry.reason ? entry.reason : 'it could not be sent.'}`)
        .join(' ')
      : '';

    if (!delivery) return `${name} ${were} sent.${how}${skippedNote}`;

    const problems: string[] = [];
    if (delivery.email && delivery.email !== 'sent' && delivery.email !== 'not requested') {
      problems.push(`Email ${delivery.email}`);
    }
    if (delivery.sms && delivery.sms !== 'sent' && delivery.sms !== 'not requested') {
      problems.push(`SMS ${delivery.sms}`);
    }
    if (!problems.length) {
      return `${name} ${were} sent to ${this.patientName}.${how}${skippedNote}`;
    }
    return `${name} ${were} created, but: ${problems.join('; ')}.${skippedNote}`;
  }

  private clearRefusal(): void {
    this.blockedMessage = null;
    this.blockedRequestId = null;
  }

  /**
   * "Go to the request that blocked this".
   *
   * Announced through the SERVICE rather than handed back through `afterClosed`, because this
   * dialog has two callers — the panel's own header button and the patient-file sidebar action —
   * and only one of them is the panel. Routing it through `focusRequest$` means
   * `patient-file.component.ts` needs to know nothing about document requests beyond opening this
   * dialog.
   */
  openBlockingRequest(): void {
    if (!this.blockedRequestId) return;
    this.patientDocumentRequestService.focusRequest(this.blockedRequestId);
    this.dialogRef.close({ focusRequestId: this.blockedRequestId });
  }

  cancel(): void {
    this.dialogRef.close();
  }
}
